// Deterministic replays of the tool loop for Lesson 4.3 ("The Model Asks the Program to Do
// Something"). Every tool call, result, token count and sentence below was recorded from real
// runs on 8 October 2026. The graphics replay those recordings and never call a model.

export type Tone = 'neutral' | 'success' | 'warning';
export interface Summary {
  message: string;
  tone: Tone;
}
export interface ToolCall {
  name: string;
  /** The arguments exactly as the model wrote them, a JSON string. */
  args: string;
}
export const STOPS = 'stops';
/** One line for a reply: each request as name(arguments), or "stops" when there is none. */
export const callsText = (calls: readonly ToolCall[]) =>
  calls.length ? calls.map((entry) => `${entry.name}(${entry.args})`).join(' and ') : STOPS;
const call = (name: string, args = '{}'): ToolCall => ({ name, args });
export const PROJECTS_RESULT = 'Apollo (1), Support (2)';

// --- tool-definition: what the request carries ----------------------------------------------

export const TOOL_SETS = ['none', 'four', 'one-word'] as const;
export type ToolSet = (typeof TOOL_SETS)[number];
export const toolSetLabels: Record<ToolSet, string> = {
  none: 'None',
  four: 'The four read tools',
  'one-word': 'The four with one-word descriptions',
};
export const toToolSet = (value: string): ToolSet =>
  TOOL_SETS.find((set) => set === value) ?? 'none';
/** The list_tasks entry exactly as the request carried it. */
export const LIST_TASKS_ENTRY = `{
  "type": "function",
  "function": {
    "name": "list_tasks",
    "description": "List the tasks of one project: id, title, done, assignee. Open tasks only, unless include_done is true. Use this to answer what is open, blocked, or assigned to whom.",
    "parameters": {
      "type": "object",
      "properties": {
        "project_id": {"type": "integer", "description": "The project's id from list_projects"},
        "include_done": {"type": "boolean", "description": "Also return completed tasks"}
      },
      "required": ["project_id"],
      "additionalProperties": false
    }
  }
}`;
export const ONE_WORD_ENTRY =
  '{"name": "list_tasks", "description": "tasks", "parameters": {"p": "integer"}}';
export const NO_TOOLS_ENTRY = '(no tools in the request)';
export interface ToolRequest {
  promptTokens: number;
  toolsSent: number;
  firstMove: string;
  /** The recorded first reply: the request the model wrote, with its sentence. */
  firstReply: string;
  entry: string | null;
}
export const toolRequests: Record<ToolSet, ToolRequest> = {
  none: {
    promptTokens: 88,
    toolsSent: 0,
    firstMove: 'asks which project you mean',
    firstReply:
      "I don't have enough context to know which project or task you're referring to. Could you please specify which project's launch you're asking about?",
    entry: null,
  },
  four: {
    promptTokens: 567,
    toolsSent: 4,
    firstMove: 'asks for list_projects',
    firstReply: `list_projects({}), with "I'll help you find what's blocking the launch. Let me start by checking your projects."`,
    entry: LIST_TASKS_ENTRY,
  },
  'one-word': {
    promptTokens: 417,
    toolsSent: 4,
    firstMove: 'asks for list_projects',
    firstReply: 'list_projects({}), measured on the question "What is open in Support?"',
    entry: ONE_WORD_ENTRY,
  },
};
export const entryText = (request: ToolRequest) => request.entry ?? NO_TOOLS_ENTRY;
export interface DefinitionState {
  set: ToolSet;
  shown: boolean;
}
export const initialDefinition = (): DefinitionState => ({ set: 'none', shown: false });
export type DefinitionAction =
  { type: 'set'; value: ToolSet } | { type: 'show' } | { type: 'reset' };
export function definitionTransition(
  state: DefinitionState,
  action: DefinitionAction,
): DefinitionState {
  switch (action.type) {
    case 'reset':
      return initialDefinition();
    case 'set':
      return { set: action.value, shown: false };
    case 'show':
      return { ...state, shown: true };
  }
}
export const DEFINITION_START_MESSAGE = 'Choose the tools sent, then press Show the request.';
export const DEFINITION_NONE_MESSAGE =
  'Without tools the request costs 88 tokens and the model asks Maya which project she means.';
export const DEFINITION_FOUR_MESSAGE =
  'Four tools cost 567 prompt tokens and the model asks for list_projects first.';
export const DEFINITION_ONE_WORD_MESSAGE =
  'One-word descriptions cost 417 prompt tokens where the full ones cost 565, and the model still asks for list_projects.';
export const DEFINITION_REFINEMENT =
  'Refinement: describe each function the model may ask for and pay its tokens on every call.';
export function definitionSummary(state: DefinitionState): Summary {
  if (!state.shown) return { tone: 'neutral', message: DEFINITION_START_MESSAGE };
  switch (state.set) {
    case 'none':
      return { tone: 'warning', message: DEFINITION_NONE_MESSAGE };
    case 'four':
      return { tone: 'success', message: DEFINITION_FOUR_MESSAGE };
    case 'one-word':
      return { tone: 'neutral', message: DEFINITION_ONE_WORD_MESSAGE };
  }
}

// --- tool-loop: the model asks, our code runs it ---------------------------------------------

export const LOOP_QUESTIONS = ['blocking', 'dates', 'sam', 'mark'] as const;
export type LoopQuestion = (typeof LOOP_QUESTIONS)[number];
export const loopQuestionLabels: Record<LoopQuestion, string> = {
  blocking: 'What is blocking the launch?',
  dates: 'Who owns the dates task, and is it done?',
  sam: 'Is Sam on Apollo?',
  mark: 'Mark the badges task as done.',
};
export const toLoopQuestion = (value: string): LoopQuestion =>
  LOOP_QUESTIONS.find((question) => question === value) ?? 'blocking';
export interface LoopStep {
  /** The requests in this reply, empty when the model stops. */
  calls: ToolCall[];
  /** What our code sent back, abridged. */
  result: string;
  promptTokens: number;
  finish: 'tool_calls' | 'stop';
}
export const ANSWER_RESULT = 'shows the answer to Maya';
const stop = (promptTokens: number): LoopStep => ({
  calls: [],
  result: ANSWER_RESULT,
  promptTokens,
  finish: 'stop',
});
const ask = (calls: ToolCall[], result: string, promptTokens: number): LoopStep => ({
  calls,
  result,
  promptTokens,
  finish: 'tool_calls',
});
const SIX_OPEN = '6 open tasks';
const BOTH_LISTS = '6 open tasks, then 1 open task';
/** Each question's recorded trace, one entry per model call. */
export const loopTraces: Record<LoopQuestion, LoopStep[]> = {
  blocking: [
    ask([call('list_projects')], PROJECTS_RESULT, 567),
    ask([call('list_tasks', '{"project_id": 1}')], SIX_OPEN, 639),
    stop(833),
  ],
  dates: [
    ask([call('list_projects')], PROJECTS_RESULT, 572),
    ask(
      [call('list_tasks', '{"project_id": 1}'), call('list_tasks', '{"project_id": 2}')],
      BOTH_LISTS,
      638,
    ),
    stop(885),
  ],
  sam: [
    ask([call('list_projects')], PROJECTS_RESULT, 566),
    ask([call('list_members', '{"project_id": 1}')], 'Maya (manager), Ravi, Jo', 611),
    stop(656),
  ],
  mark: [stop(568)],
};
/** The final answer each question received, verbatim. */
export const loopAnswers: Record<LoopQuestion, string> = {
  blocking:
    'The Apollo project has 6 open tasks that need completion: writing release notes (assigned to Ravi), checking dates, ordering name badges, checking the projector, sending final headcount to the venue, and writing the release note for Apollo. All of these tasks are currently incomplete and could be blocking the launch.',
  dates:
    'The "Check the dates" task (id 10) is not done and has no assignee. It\'s in the Apollo project.',
  sam: 'No, Sam is not on the Apollo project. The members are Maya (manager), Ravi (member), and Jo (member).',
  mark: "I don't have a tool that can mark tasks as done. I can only view your projects and tasks, not modify them. You'll need to mark the task as done directly in CollabBoard.",
};
export interface LoopState {
  question: LoopQuestion;
  /** How many steps of the trace have been pressed into view. */
  shown: number;
}
export const initialLoop = (): LoopState => ({ question: 'blocking', shown: 0 });
export type LoopAction =
  { type: 'question'; value: LoopQuestion } | { type: 'next' } | { type: 'reset' };
export function loopTransition(state: LoopState, action: LoopAction): LoopState {
  switch (action.type) {
    case 'reset':
      return initialLoop();
    case 'question':
      return { question: action.value, shown: 0 };
    case 'next':
      return { ...state, shown: Math.min(state.shown + 1, loopTraces[state.question].length) };
  }
}
export const shownSteps = (state: LoopState) => loopTraces[state.question].slice(0, state.shown);
export const currentStep = (state: LoopState): LoopStep | null =>
  loopTraces[state.question][state.shown - 1] ?? null;
export const loopDone = (state: LoopState) => state.shown === loopTraces[state.question].length;
export const stepLabel = (state: LoopState) =>
  `${state.shown} of ${loopTraces[state.question].length}`;
export const LOOP_START_MESSAGE = 'Choose a question, then press Next step.';
export const loopStepMessage = (step: number, name: string) =>
  `Step ${step}: the model asks for ${name}, and our code runs it.`;
export const LOOP_TWO_REQUESTS_MESSAGE =
  'Two requests in one reply, and our code runs both before the next call.';
export const loopDoneMessage = (steps: number) =>
  `Done in ${steps} steps, and the loop ended on finish_reason stop.`;
export const LOOP_COMMAND_MESSAGE =
  'One step and no tool call: no tool can mark tasks as done, so the model says so.';
export const LOOP_REFINEMENT =
  'Refinement: the model asks, code runs, and the loop continues until stop.';
export function loopSummary(state: LoopState): Summary {
  const step = currentStep(state);
  if (!step) return { tone: 'neutral', message: LOOP_START_MESSAGE };
  if (step.finish === 'tool_calls')
    return step.calls.length > 1
      ? { tone: 'neutral', message: LOOP_TWO_REQUESTS_MESSAGE }
      : { tone: 'neutral', message: loopStepMessage(state.shown, step.calls[0]?.name ?? STOPS) };
  if (state.question === 'mark') return { tone: 'warning', message: LOOP_COMMAND_MESSAGE };
  return { tone: 'success', message: loopDoneMessage(state.shown) };
}

// --- tool-error: a failure is a result ------------------------------------------------------

export const FAILURES = ['missing-task', 'omitted-id', 'database', 'no-projects'] as const;
export type FailureKind = (typeof FAILURES)[number];
export const failureLabels: Record<FailureKind, string> = {
  'missing-task': 'Task 999 not found',
  'omitted-id': 'project_id left out',
  database: 'The database is unavailable',
  'no-projects': 'Sam has no projects',
};
export const toFailure = (value: string): FailureKind =>
  FAILURES.find((failure) => failure === value) ?? 'missing-task';
export interface Failure {
  /** The request that failed. */
  request: string;
  /** The result the loop sent back, exactly. */
  result: string;
  nextMove: 'stops' | 'asks again';
  /** The model's recorded reply: its sentence, or the requests it wrote next. */
  reply: string;
}
export const failures: Record<FailureKind, Failure> = {
  'missing-task': {
    request: 'get_task({"task_id": 999})',
    result: '{"error": "Task 999 was not found"}',
    nextMove: 'stops',
    reply: 'Task 999 was not found.',
  },
  'omitted-id': {
    request: 'list_tasks({})',
    result: '{"error": "list_tasks needs project_id (an integer from list_projects)."}',
    nextMove: 'asks again',
    reply: 'list_tasks({"project_id": 1}) and list_tasks({"project_id": 2})',
  },
  database: {
    request: 'list_tasks({"project_id": 1})',
    result: '{"error": "The task database is unavailable. Try reading again after it recovers."}',
    nextMove: 'stops',
    reply:
      'I can see you have a "Apollo" project, but the task database is currently unavailable. Please try again in a moment when it recovers.',
  },
  'no-projects': {
    request: 'list_projects({}), as Sam',
    result: '[]',
    nextMove: 'stops',
    reply:
      "I don't see any projects available. It appears you may not be signed in or don't have access to any projects yet.",
  },
};
export const finishReason = (failure: Failure) =>
  failure.nextMove === 'stops' ? 'stop' : 'tool_calls';
export const NO_EXCEPTION = 'none';
export interface ErrorState {
  failure: FailureKind;
  sent: boolean;
}
export const initialError = (): ErrorState => ({ failure: 'missing-task', sent: false });
export type ErrorAction =
  { type: 'failure'; value: FailureKind } | { type: 'send' } | { type: 'reset' };
export function errorTransition(state: ErrorState, action: ErrorAction): ErrorState {
  switch (action.type) {
    case 'reset':
      return initialError();
    case 'failure':
      return { failure: action.value, sent: false };
    case 'send':
      return { ...state, sent: true };
  }
}
export const ERROR_START_MESSAGE = 'Choose a failure, then press Send the result.';
export const errorMessages: Record<FailureKind, string> = {
  'missing-task': 'The model read the error and told Maya that task 999 was not found.',
  'omitted-id': 'The model read its own mistake and asked again with a project_id.',
  database: 'The model turned the error into an honest sentence and stopped.',
  'no-projects': 'An empty list is a result too, so Sam hears there are no projects.',
};
export const ERROR_REFINEMENT = 'Refinement: a failure is a result, and the loop never raises.';
export function errorSummary(state: ErrorState): Summary {
  if (!state.sent) return { tone: 'neutral', message: ERROR_START_MESSAGE };
  return { tone: 'success', message: errorMessages[state.failure] };
}

// --- tool-budget: the loop needs a stop ------------------------------------------------------

export const BUDGETS = ['2', '6'] as const;
export type Budget = (typeof BUDGETS)[number];
export const budgetLabels: Record<Budget, string> = { '2': '2 steps', '6': '6 steps' };
export const toBudget = (value: string): Budget =>
  BUDGETS.find((budget) => budget === value) ?? '6';
export const BUDGET_QUESTION = 'Which tasks are assigned to Jo across all my projects?';
/** The recorded trace under each budget, one entry per model call. */
export const budgetTraces: Record<Budget, LoopStep[]> = {
  '6': [
    ask([call('list_projects')], PROJECTS_RESULT, 572),
    ask(
      [call('list_tasks', '{"project_id": 1}'), call('list_tasks', '{"project_id": 2}')],
      BOTH_LISTS,
      639,
    ),
    stop(886),
  ],
  '2': [
    ask([call('list_projects')], PROJECTS_RESULT, 572),
    ask(
      [call('list_tasks', '{"project_id": 1}'), call('list_tasks')],
      '6 open tasks, then an error for the missing project_id',
      639,
    ),
  ],
};
export const BUDGET_ANSWER =
  'Jo has one task assigned: "Answer the refund tickets" (task 16) in the Support project.';
export const BUDGET_STOP_SENTENCE =
  'I stopped after 2 steps without an answer. Here is what I looked at.';
export const budgetOutcome = (budget: Budget) =>
  budget === '6' ? 'answered' : 'stopped by the budget';
export const mayaSees = (budget: Budget) => (budget === '6' ? BUDGET_ANSWER : BUDGET_STOP_SENTENCE);
export const stepsUsed = (budget: Budget) => `${budgetTraces[budget].length} of ${budget}`;
export const lastPromptTokens = (steps: readonly LoopStep[]) => steps.at(-1)?.promptTokens ?? 0;
export interface BudgetState {
  budget: Budget;
  ran: boolean;
}
export const initialBudget = (): BudgetState => ({ budget: '6', ran: false });
export type BudgetAction = { type: 'budget'; value: Budget } | { type: 'run' } | { type: 'reset' };
export function budgetTransition(state: BudgetState, action: BudgetAction): BudgetState {
  switch (action.type) {
    case 'reset':
      return initialBudget();
    case 'budget':
      return { budget: action.value, ran: false };
    case 'run':
      return { ...state, ran: true };
  }
}
export const BUDGET_START_MESSAGE = 'Choose a step budget, then press Run.';
export const BUDGET_ANSWERED_MESSAGE =
  'Three steps and an answer, and the prompt grew from 572 to 886 tokens.';
export const BUDGET_STOPPED_MESSAGE =
  'The budget stopped the loop after two steps, so Maya sees the partial trace.';
export const BUDGET_REFINEMENT =
  'Refinement: a budget, the partial trace shown, and the stop logged.';
export function budgetSummary(state: BudgetState): Summary {
  if (!state.ran) return { tone: 'neutral', message: BUDGET_START_MESSAGE };
  return state.budget === '6'
    ? { tone: 'success', message: BUDGET_ANSWERED_MESSAGE }
    : { tone: 'warning', message: BUDGET_STOPPED_MESSAGE };
}
